import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Search, UserPlus, Upload, X, ShieldCheck, Crown, CheckCircle2, Building2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ScoreRing } from "../components/ScoreRing";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import api, { formatApiErrorDetail } from "../lib/api";

const CH = {
  scalp_itching: ["none", "mild", "moderate", "severe"],
  dandruff: ["none", "some", "heavy"],
};

const Pills = ({ value, onChange, options, labels, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)}
        className={`rounded-full border px-4 py-2 text-sm capitalize transition-colors ${value === o ? "border-gold bg-gold/10 text-gold" : "border-charcoal-border text-cream hover:border-gold/40"}`}>
        {labels ? labels[o] : o}
      </button>
    ))}
  </div>
);

export default function AdminScan() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "" });
  const [photo, setPhoto] = useState(null);
  const [form, setForm] = useState({
    time_since_service: "", wash_frequency: "",
    scalp_itching: "none", dandruff: "none", client_concerns: "",
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/clients", { params: q.trim() ? { q: q.trim() } : {} })
        .then((r) => setClients(r.data.clients)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const createClient = async () => {
    if (!newClient.name.trim() || !newClient.email.trim()) { toast.error("Enter name and email."); return; }
    try {
      const { data } = await api.post("/admin/clients", newClient);
      setClient({ id: data.id, name: data.name, email: data.email });
      setShowNew(false);
      toast.success(data.existing ? "Existing client selected." : "Client profile created.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(jpeg|png|webp|jpg)/.test(f.type)) { toast.error("Use a JPEG, PNG or WEBP image."); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!client) { toast.error("Select or create a client first."); return; }
    if (!photo) { toast.error("Add a scalp photo."); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/admin/scans", { client_id: client.id, photo_base64: photo, questionnaire: form });
      setResult(data.result);
      toast.success("Scan saved to client's profile.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setAnalyzing(false); }
  };

  if (analyzing)
    return (
      <div className="min-h-screen bg-charcoal text-cream">
        <Navbar />
        <div className="mx-auto flex max-w-md flex-col items-center px-6 py-32 text-center">
          <div className="relative mb-8"><Crown className="h-12 w-12 text-gold" strokeWidth={1} /><div className="absolute inset-0 animate-ping opacity-30"><Crown className="h-12 w-12 text-gold" strokeWidth={1} /></div></div>
          <h1 className="font-heading text-3xl text-cream">Scalp Analyzing — {client?.name}</h1>
          <p className="mt-3 text-sm text-muted-foreground">Please wait — it can take up to 5 minutes. Don't close the page.</p>
          <Loader2 className="mt-8 h-6 w-6 animate-spin text-gold" />
        </div>
      </div>
    );

  if (result)
    return (
      <div className="min-h-screen bg-charcoal text-cream">
        <Navbar />
        <div className="mx-auto max-w-2xl px-6 py-16 text-center" data-testid="admin-scan-result">
          <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-gold" />
          <h1 className="font-heading text-4xl text-cream">Scan complete</h1>
          <p className="mt-2 text-sm text-muted-foreground">Saved to <span className="text-cream">{client.name}</span>'s profile · <span className="inline-flex items-center gap-1 text-gold"><Building2 className="h-3.5 w-3.5" /> In-Salon</span> · Paid — included in appointment.</p>
          <div className="mt-8 flex justify-center"><ScoreRing score={result.overall_score} label={result.overall_label} /></div>
          <div className="mt-8 flex justify-center gap-3">
            <Button className="rounded-full bg-gold text-charcoal hover:bg-gold/90" onClick={() => { setResult(null); setPhoto(null); setClient(null); }} data-testid="admin-scan-another">Scan another client</Button>
            <Button variant="ghost" className="rounded-full border border-charcoal-border text-cream hover:text-gold" onClick={() => navigate("/admin")}>Back to console</Button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="mx-auto max-w-3xl px-6 py-12">
        <span className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold"><ShieldCheck className="h-4 w-4" /> In-Salon Scan</span>
        <h1 className="mt-2 font-heading text-4xl text-cream md:text-5xl">Perform a client scan</h1>
        <p className="mt-2 text-muted-foreground">No payment here — the scan fee is bundled into the in-salon appointment.</p>

        {/* Client */}
        <div className="mt-8 rounded-2xl border border-charcoal-border bg-charcoal-surface p-6">
          <Label className="mb-3 block text-cream">Client</Label>
          {client ? (
            <div className="flex items-center justify-between rounded-lg border border-gold/40 bg-charcoal p-4" data-testid="selected-client">
              <div><p className="text-cream">{client.name}</p><p className="text-xs text-muted-foreground">{client.email}</p></div>
              <button className="text-xs text-gold hover:underline" onClick={() => setClient(null)} data-testid="change-client-btn">Change</button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search existing clients by name or email…" className="border-charcoal-border bg-charcoal pl-9 text-cream focus-visible:ring-gold" data-testid="client-search-input" />
              </div>
              <div className="mt-3 max-h-48 space-y-2 overflow-y-auto" data-testid="client-results">
                {clients.map((c) => (
                  <button key={c.id} onClick={() => setClient(c)} className="flex w-full items-center justify-between rounded-lg border border-charcoal-border bg-charcoal p-3 text-left hover:border-gold/40" data-testid={`client-option-${c.id}`}>
                    <div><p className="text-sm text-cream">{c.name}</p><p className="text-xs text-muted-foreground">{c.email}</p></div>
                    <ArrowRight className="h-4 w-4 text-gold" />
                  </button>
                ))}
              </div>
              {showNew ? (
                <div className="mt-4 space-y-3 rounded-lg border border-gold/30 p-4" data-testid="new-client-form">
                  <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} placeholder="Full name" className="border-charcoal-border bg-charcoal text-cream" data-testid="new-client-name" />
                  <Input value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} placeholder="Email" className="border-charcoal-border bg-charcoal text-cream" data-testid="new-client-email" />
                  <div className="flex gap-2">
                    <Button className="rounded-full bg-gold text-charcoal hover:bg-gold/90" onClick={createClient} data-testid="create-client-btn">Create & select</Button>
                    <Button variant="ghost" className="rounded-full text-muted-foreground" onClick={() => setShowNew(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <button className="mt-4 flex items-center gap-2 text-sm text-gold hover:underline" onClick={() => setShowNew(true)} data-testid="add-client-btn">
                  <UserPlus className="h-4 w-4" /> New client not registered? Create a profile
                </button>
              )}
            </>
          )}
        </div>

        {/* Photo */}
        <div className="mt-6 rounded-2xl border border-charcoal-border bg-charcoal-surface p-6">
          <Label className="mb-3 block text-cream">Scalp / loc-roots photo</Label>
          {photo ? (
            <div className="relative overflow-hidden rounded-xl border border-charcoal-border">
              <img src={photo} alt="preview" className="max-h-72 w-full object-cover" data-testid="admin-photo-preview" />
              <button onClick={() => setPhoto(null)} className="absolute right-3 top-3 rounded-full bg-black/70 p-2 text-cream hover:text-gold"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className="flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-charcoal-border py-12 hover:border-gold/50" data-testid="admin-upload-zone">
              <Upload className="mb-2 h-6 w-6 text-gold" /><span className="text-sm text-muted-foreground">Upload or capture a close-up</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="admin-photo-input" />
        </div>

        {/* Questionnaire */}
        <div className="mt-6 space-y-6 rounded-2xl border border-charcoal-border bg-charcoal-surface p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div><Label className="mb-2 block text-cream">Last time client had a service</Label><Input value={form.time_since_service} onChange={(e) => setForm({ ...form, time_since_service: e.target.value })} placeholder="e.g. 3 weeks ago" className="border-charcoal-border bg-charcoal text-cream" data-testid="a-last-service" /></div>
            <div><Label className="mb-2 block text-cream">How often they wash their hair</Label><Input value={form.wash_frequency} onChange={(e) => setForm({ ...form, wash_frequency: e.target.value })} placeholder="e.g. Weekly" className="border-charcoal-border bg-charcoal text-cream" data-testid="a-wash" /></div>
          </div>
          <div><Label className="mb-2 block text-cream">Scalp itching</Label><Pills value={form.scalp_itching} onChange={(v) => setForm({ ...form, scalp_itching: v })} options={CH.scalp_itching} testid="a-itching" /></div>
          <div><Label className="mb-2 block text-cream">Dandruff</Label><Pills value={form.dandruff} onChange={(v) => setForm({ ...form, dandruff: v })} options={CH.dandruff} testid="a-dandruff" /></div>
          <div><Label className="mb-2 block text-cream">Client concerns</Label><Textarea value={form.client_concerns} onChange={(e) => setForm({ ...form, client_concerns: e.target.value })} placeholder="Anything the client mentioned…" className="border-charcoal-border bg-charcoal text-cream" data-testid="a-concerns" /></div>
        </div>

        <Button onClick={submit} className="mt-8 w-full rounded-full bg-gold py-6 text-base font-500 text-charcoal hover:bg-gold/90" data-testid="admin-submit-scan-btn">
          <Crown className="mr-2 h-4 w-4" /> Generate report for {client?.name || "client"}
        </Button>
      </div>
      <Footer />
    </div>
  );
}
